import { ApplicationUserDto } from '../models/auth';
import { ROLES } from '@/lib/constants/roles';
import type { Role } from '@/lib/constants/roles';

/**
 * Array di nomi di ruoli
 */
export type RoleNames = Role[] | string[];

/**
 * Utility functions centrali per la gestione dell'autenticazione e autorizzazione
 *
 * Queste funzioni implementano la logica di business per la verifica dei ruoli
 * e sono condivise tra il mixin Options API e il composable Composition API.
 *
 * @category Authentication Utils
 * @since 1.0.0
 */
export class AuthUtils {
	/**
	 * Verifica se l'utente corrente possiede un ruolo specifico
	 *
	 * @param userInfo - Informazioni dell'utente corrente
	 * @param roleName - Nome del ruolo da verificare (usare ROLES.* per type safety)
	 * @returns true se l'utente ha il ruolo specificato, false altrimenti
	 */
	static isInRole(userInfo: ApplicationUserDto | null, roleName: Role | string): boolean {
		if (!userInfo || !userInfo.roles) {
			return false;
		}

		if (!Array.isArray(userInfo.roles) || userInfo.roles.length === 0) {
			return false;
		}

		return userInfo.roles.some(
			(userRole: any) => userRole.name === roleName || userRole === roleName,
		);
	}

	/**
	 * Verifica se l'utente corrente possiede almeno uno dei ruoli specificati
	 *
	 * @param userInfo - Informazioni dell'utente corrente
	 * @param roleNames - Array di nomi di ruoli da verificare
	 * @returns true se l'utente ha almeno uno dei ruoli specificati, false altrimenti
	 */
	static isInRoles(userInfo: ApplicationUserDto | null, roleNames: RoleNames): boolean {
		if (!userInfo || !userInfo.roles) {
			return false;
		}

		if (!Array.isArray(roleNames) || roleNames.length === 0) {
			return false;
		}

		return userInfo.roles.some((userRole: any) => roleNames.includes(userRole.name || userRole));
	}

	/**
	 * Verifica se l'utente è un "pure manager" (solo manager, senza privilegi admin)
	 *
	 * @param userInfo - Informazioni dell'utente corrente
	 * @returns true se l'utente è solo Manager (senza ruoli admin), false altrimenti
	 */
	static isPureManager(userInfo: ApplicationUserDto | null): boolean {
		if (!userInfo || !userInfo.roles) {
			return false;
		}

		const hasManagerRole = AuthUtils.isInRole(userInfo, ROLES.MANAGER);
		const hasAdminRole = AuthUtils.isInRole(userInfo, ROLES.ADMIN);
		const hasSysAdminRole = AuthUtils.isInRole(userInfo, ROLES.SYS_ADMIN);

		return hasManagerRole && !hasAdminRole && !hasSysAdminRole;
	}
}
